import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { classNames } from 'primereact/utils';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import globalSettingsStores from '../../../../stores/settings-stores';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import globalUserPage from '../../../../stores/global-user-page';
import { toast } from 'react-toastify';
import { Tooltip } from 'primereact/tooltip';
import { CreditCardTokenEntity } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { CreditCardSelection } from './components/credit-card-selection';
import { AddNewCardDialog } from './components/add-new-card-dialog';
import { PaymentMethodForm } from './components/payment-method-form';
import { submitPaymentOperation } from './operations/submit-payment-operation';
import phoneNumberUtils from '../../../../utils/international-to-israeli';

// Payment method types
export enum PaymentMethod {
  CreditCard = 'credit_card',
  BankTransfer = 'bank_transfer',
  Check = 'check',
  Scholarship = 'scholarship',
  Cash = 'cash',
  CreditCardInstallments = 'credit_card_installments',
  BankTransferInstallments = 'bank_transfer_installments',
  ExistingCreditCardInstallments = 'existing_credit_card_installments',
  ExistingBankTransferInstallments = 'existing_bank_transfer_installments',
}

export interface AllocationWithAmount {
  allocationId: number;
  paymentServiceName: string;
  maxAmount: number;
  amount: number;
}

export interface CheckPaymentDetails {
  referenceId: string;
  amount: number;
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
  dueDate?: Date;
  allocations: {
    allocationId: number;
    amount: number;
  }[];
}

/** Default «לכבוד»: full name and national ID when present (max 100 chars). */
function buildDefaultReceiptRecipientName(user: UserSummaryType): string {
  const name = `${user.firstName} ${user.lastName}`.trim();
  const id = (user.nationalId ?? '').trim();
  if (!id) {
    return name.slice(0, 100);
  }
  const combined = `${name}`;
  return combined.slice(0, 100);
}

interface PaymentMethodSelectionDialogProps {
  visible: boolean;
  onHide: () => void;
  allocations: AllocationWithAmount[];
  user: UserSummaryType;
  onPaymentMethodSelected: (
    method: PaymentMethod,
    allocations: AllocationWithAmount[],
    additionalData?: any,
  ) => void;
}

export const PaymentMethodSelectionDialog: React.FC<
  PaymentMethodSelectionDialogProps
> = ({ visible, onHide, allocations, user, onPaymentMethodSelected }) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [allocationAmounts, setAllocationAmounts] = useState<
    AllocationWithAmount[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [receiptRecipientName, setReceiptRecipientName] = useState('');
  const [receiptIdentifierId, setReceiptIdentifierId] = useState('');
  const [receiptPhoneNumber, setReceiptPhoneNumber] = useState('');
  const [receiptIssuedFor, setReceiptIssuedFor] = useState('');
  const [receiptNote, setReceiptNote] = useState('');

  // Initialize allocation amounts when dialog opens or allocations change
  React.useEffect(() => {
    if (visible && allocations.length > 0) {
      setAllocationAmounts(
        allocations.map((alloc) => ({
          ...alloc,
          // Start at 0 so the user must enter amounts explicitly (avoids accidental full-max charges).
          amount: 0,
        })),
      );
    }
  }, [visible, allocations]);

  useEffect(() => {
    if (!visible) {
      setSelectedMethod(null);
      setAllocationAmounts([]);
      setReceiptRecipientName('');
      setReceiptIdentifierId('');
      setReceiptPhoneNumber('');
      setReceiptIssuedFor('');
      setReceiptNote('');
      setChecks([]);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && user) {
      setReceiptRecipientName(buildDefaultReceiptRecipientName(user));
      setReceiptIdentifierId((user.nationalId ?? '').trim());
      setReceiptPhoneNumber(
        phoneNumberUtils.internationalToIsraeli(
          (user.phoneNumber ?? '').trim(),
        ),
      );
      setReceiptIssuedFor('');
      setReceiptNote('');
    }
  }, [
    visible,
    user.id,
    user.firstName,
    user.lastName,
    user.nationalId,
    user.phoneNumber,
  ]);

  // Bank Transfer Form State
  const [bankTransferData, setBankTransferData] = useState({
    bankNumber: '',
    branchNumber: '',
    accountNumber: '',
    transferDate: null as Date | null,
    referenceId: '',
  });

  // Check Form State
  const [checkData, setCheckData] = useState({
    checkNumber: '',
    amount: null as number | null,
    bankNumber: '',
    branchNumber: '',
    accountNumber: '',
    clearingDate: null as Date | null,
  });
  const [checks, setChecks] = useState<CheckPaymentDetails[]>([]);

  // Scholarship Form State
  const [scholarshipData, setScholarshipData] = useState({
    scholarshipId: null as number | null,
    studentScholarshipId: null as number | null,
  });

  // Credit Card Form State
  const [creditCardData, setCreditCardData] = useState({
    selectedCreditCardTokenId: null as number | null,
    numberOfInstallments: 1 as number,
  });

  // Credit Card Installments Form State
  const [installmentsData, setInstallmentsData] = useState({
    numberOfInstallments: 1 as number,
    selectedCreditCardTokenId: null as number | null,
    firstPaymentMonth: null as { month: number; year: number } | null,
  });

  // Add New Card Dialog State
  const [showAddNewCardDialog, setShowAddNewCardDialog] = useState(false);

  // Get existing credit card tokens
  const creditCardTokens: CreditCardTokenEntity[] =
    globalUserPage.studentDetails?.creditCardTokens?.filter(
      (token) => token.isActive,
    ) ?? [];

  // Bank Transfer Installments Form State
  const [bankTransferInstallmentsData, setBankTransferInstallmentsData] =
    useState({
      numberOfInstallments: 1 as number,
      bankNumber: '',
      branchNumber: '',
      accountNumber: '',
      firstPaymentMonth: null as { month: number; year: number } | null,
    });

  // Existing Installment Plan Selection State
  const [
    selectedExistingInstallmentPlanId,
    setSelectedExistingInstallmentPlanId,
  ] = useState<number | null>(null);
  const [
    selectedExistingBankTransferPlanId,
    setSelectedExistingBankTransferPlanId,
  ] = useState<number | null>(null);

  // Get existing installment plans from user details
  const existingCreditCardInstallments =
    globalUserPage.studentDetails?.installments ?? [];
  const existingBankTransferInstallments =
    globalUserPage.studentDetails?.installmentsBankTransfer ?? [];

  const basePaymentMethods = [
    {
      type: PaymentMethod.CreditCard,
      icon: <i className="pi pi-credit-card text-2xl"></i>,
      label: 'כרטיס אשראי',
      description: 'תשלום מאובטח באמצעות כרטיס אשראי',
      color: '#00C853',
    },
    {
      type: PaymentMethod.BankTransfer,
      icon: <i className="pi pi-money-bill text-2xl"></i>,
      label: 'העברה בנקאית',
      description: 'העברה ישירה מחשבון הבנק שלך',
      color: '#2196F3',
    },
    {
      type: PaymentMethod.Check,
      icon: <i className="pi pi-file-edit text-2xl"></i>,
      label: 'המחאות',
      description: 'תשלום באמצעות המחאה',
      color: '#9C27B0',
    },
    {
      type: PaymentMethod.Scholarship,
      icon: <i className="pi pi-bookmark text-2xl"></i>,
      label: 'מלגות',
      description: 'תשלום באמצעות מלגה',
      color: '#FF9800',
    },
    {
      type: PaymentMethod.Cash,
      icon: <i className="pi pi-wallet text-2xl"></i>,
      label: 'מזומן',
      description: 'תשלום במזומן',
      color: '#00C853',
    },
    {
      type: PaymentMethod.CreditCardInstallments,
      icon: <i className="pi pi-calendar text-2xl"></i>,
      label: 'הוראת קבע באשראי',
      description: 'תשלומים חודשיים קבועים בכרטיס אשראי',
      color: '#E91E63',
    },
    {
      type: PaymentMethod.BankTransferInstallments,
      icon: <i className="pi pi-sync text-2xl"></i>,
      label: 'הוראת קבע בנקאית',
      description: 'תשלומים חודשיים קבועים בהעברה בנקאית',
      color: '#3F51B5',
    },
  ];

  // Add existing installment options if available
  const paymentMethods = [
    ...basePaymentMethods,
    ...(existingCreditCardInstallments.length > 0
      ? [
          {
            type: PaymentMethod.ExistingCreditCardInstallments,
            icon: <i className="pi pi-plus-circle text-2xl"></i>,
            label: 'הוסף להוראת קבע באשראי קיימת',
            description: `${existingCreditCardInstallments.length} הוראות קבע פעילות`,
            color: '#C2185B',
          },
        ]
      : []),
    ...(existingBankTransferInstallments.length > 0
      ? [
          {
            type: PaymentMethod.ExistingBankTransferInstallments,
            icon: <i className="pi pi-plus-circle text-2xl"></i>,
            label: 'הוסף להוראת קבע בנקאית קיימת',
            description: `${existingBankTransferInstallments.length} הוראות קבע פעילות`,
            color: '#303F9F',
          },
        ]
      : []),
  ];

  const [hasInvalidAmount, setHasInvalidAmount] = useState<
    AllocationWithAmount[]
  >([]);

  useEffect(() => {
    const hasInvalidAmount = allocationAmounts.filter(
      (alloc) =>
        !alloc.amount || alloc.amount <= 0 || alloc.amount > alloc.maxAmount,
    );

    setHasInvalidAmount(hasInvalidAmount);
  }, [allocationAmounts]);

  const handleSubmit = async () => {
    await submitPaymentOperation({
      selectedMethod,
      allocationAmounts,
      user,
      creditCardData,
      bankTransferData,
      checkData,
      checks,
      scholarshipData,
      installmentsData,
      bankTransferInstallmentsData,
      selectedExistingInstallmentPlanId,
      selectedExistingBankTransferPlanId,
      recipientName: receiptRecipientName,
      identifierId: receiptIdentifierId,
      phoneNumber: receiptPhoneNumber,
      issuedFor: receiptIssuedFor,
      note: receiptNote,
      setLoading,
      onHide,
    });
  };

  return (
    <Dialog
      header="בחירת אמצעי תשלום"
      visible={visible}
      onHide={onHide}
      style={{
        height: '95vh',
        minWidth: '1100px',
        width: '60vw',
        maxWidth: '1600px',
      }}
      footer={
        selectedMethod && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'end',
              width: '100%',
              gap: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%',
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
              }}
            >
              <label className="font-medium" style={{ fontSize: '16px' }}>
                סכומים לתשלום:
              </label>
              {allocationAmounts.map((allocation, index) => (
                <div
                  key={allocation.allocationId}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      textAlign: 'right',
                    }}
                  >
                    {allocation.paymentServiceName}
                  </span>

                  <InputNumber
                    value={allocation.amount}
                    prefix={'₪'}
                    inputStyle={{ textAlign: 'right' }}
                    onChange={(e) => {
                      const newAmounts = [...allocationAmounts];
                      newAmounts[index] = {
                        ...newAmounts[index],
                        amount: e.value || 0,
                      };
                      const total = newAmounts.reduce(
                        (sum, item) => sum + item.amount,
                        0,
                      );
                      if (total > allocation.maxAmount) {
                        toast.info(
                          `לא ניתן לחרוג לשרות התשלום ${allocation.paymentServiceName} יותר מ ${allocation.maxAmount}`,
                        );
                      }
                      setAllocationAmounts(newAmounts);
                    }}
                    /*mode="currency"
                    currency="ILS"*/
                    locale="he-IL"
                    /*max={allocation.maxAmount}*/
                    min={0}
                    style={{ width: '200px', direction: 'ltr' }}
                  />
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const newAmounts = [...allocationAmounts];
                      newAmounts[index] = {
                        ...newAmounts[index],
                        amount: 0,
                      };
                      setAllocationAmounts(newAmounts);
                    }}
                    data-pr-tooltip="לחץ לניכוי"
                    data-pr-position="top"
                  >
                    <i
                      className="pi pi-times-circle"
                      style={{ color: '#666' }}
                    />
                  </span>
                  <Tooltip target="[data-pr-tooltip]" />

                  <span style={{ fontSize: '12px', color: '#666' }}>
                    מתוך ₪{allocation.maxAmount}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px',
                  borderTop: '2px solid #2196F3',
                  fontWeight: 'bold',
                }}
              >
                <span>סה"כ:</span>
                <span>
                  ₪
                  {allocationAmounts
                    .reduce((sum, alloc) => sum + (alloc.amount || 0), 0)
                    .toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </span>
              </div>
            </div>
            <Button
              label="המשך לתשלום"
              icon="pi pi-check"
              loading={loading}
              disabled={hasInvalidAmount.length > 0}
              tooltip={
                hasInvalidAmount.length > 0
                  ? !hasInvalidAmount[0].amount ||
                    hasInvalidAmount[0].amount <= 0
                    ? `יש להזין סכום גדול מ-0 עבור ${hasInvalidAmount[0].paymentServiceName}`
                    : `שים לב, לא ניתן לשלם עבור השרות ${hasInvalidAmount[0].paymentServiceName} יותר מ ${hasInvalidAmount[0].maxAmount}`
                  : `לחץ לתשלום`
              }
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              onClick={handleSubmit}
              className="p-button-raised"
            />
          </div>
        )
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
          width: '100%',
        }}
      >
        {paymentMethods.map((method) => {
          const isSelected = selectedMethod === method.type;
          return (
            <div
              key={method.type}
              onClick={() => setSelectedMethod(method.type)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 16px',
                backgroundColor: isSelected ? `${method.color}08` : '#ffffff',
                border: `2px solid ${isSelected ? method.color : '#e5e7eb'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isSelected
                  ? `0 2px 8px ${method.color}25`
                  : '0 1px 3px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = `${method.color}80`;
                  e.currentTarget.style.backgroundColor = `${method.color}05`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              {/* Checkbox indicator on the left */}
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  border: `2px solid ${isSelected ? method.color : '#d1d5db'}`,
                  backgroundColor: isSelected ? method.color : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                }}
              >
                {isSelected && (
                  <i
                    className="pi pi-check"
                    style={{ color: 'white', fontSize: '12px' }}
                  />
                )}
              </div>

              {/* Text content in the middle */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: isSelected ? method.color : '#374151',
                    marginBottom: '2px',
                    transition: 'color 0.2s ease',
                  }}
                >
                  {method.label}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {method.description}
                </div>
              </div>

              {/* Icon on the right */}
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  backgroundColor: isSelected
                    ? `${method.color}15`
                    : `${method.color}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  color: method.color,
                }}
              >
                {method.icon}
              </div>
            </div>
          );
        })}
      </div>

      {selectedMethod && (
        <div className="mt-6">
          <style>
            {`
              @media (max-width: 767px) {
                .payment-method-dialog-receipt-payment-grid {
                  grid-template-columns: minmax(0, 1fr) !important;
                }
              }

              .payment-method-dialog-receipt-panel {
                border-radius: 12px;
                border: 1px solid #e2e8f0;
                background: linear-gradient(165deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%);
                box-shadow:
                  0 4px 6px -1px rgba(15, 23, 42, 0.07),
                  0 2px 4px -2px rgba(15, 23, 42, 0.05);
                border-inline-start: 4px solid #0d9488;
                padding: 1.125rem 1.25rem;
                min-width: 0;
                width: 100%;
                box-sizing: border-box;
              }

              .payment-method-dialog-receipt-panel .receipt-panel-header {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
                margin-bottom: 1rem;
              }

              .payment-method-dialog-receipt-panel .receipt-panel-icon-wrap {
                flex-shrink: 0;
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 10px;
                background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
                color: #0f766e;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.125rem;
                box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
              }

              .payment-method-dialog-receipt-panel .receipt-panel-title {
                margin: 0;
                font-size: 1.125rem;
                font-weight: 700;
                color: #0f172a;
                letter-spacing: -0.02em;
                line-height: 1.3;
              }

              .payment-method-dialog-receipt-panel .receipt-panel-kicker {
                display: block;
                font-size: 0.75rem;
                font-weight: 500;
                color: #64748b;
                margin-top: 0.2rem;
              }

              .payment-method-dialog-receipt-panel .receipt-panel-info {
                display: flex;
                align-items: flex-start;
                gap: 0.5rem;
                font-size: 0.8125rem;
                line-height: 1.65;
                color: #475569;
                background: #f0fdfa;
                border: 1px solid #99f6e4;
                border-radius: 8px;
                padding: 0.75rem 0.875rem;
                margin-bottom: 1.125rem;
              }

              .payment-method-dialog-receipt-panel .receipt-panel-info .pi-info-circle {
                flex-shrink: 0;
                margin-top: 0.12rem;
                color: #0d9488;
                font-size: 0.95rem;
              }

              .payment-method-dialog-receipt-panel .receipt-field-group {
                margin-bottom: 1rem;
                width: 100%;
                min-width: 0;
                box-sizing: border-box;
              }

              .payment-method-dialog-receipt-panel .receipt-field-group:last-child {
                margin-bottom: 0;
              }

              .payment-method-dialog-receipt-panel .receipt-field-label {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.35rem;
                font-size: 0.875rem;
                font-weight: 600;
                color: #334155;
                margin-bottom: 0.4rem;
              }

              .payment-method-dialog-receipt-panel .receipt-optional-pill {
                font-size: 0.65rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: #64748b;
                background: #e2e8f0;
                padding: 0.15rem 0.45rem;
                border-radius: 4px;
              }

              .payment-method-dialog-receipt-panel .receipt-field-hint {
                font-size: 0.6875rem;
                color: #94a3b8;
                margin-top: 0.3rem;
              }

              .payment-method-dialog-receipt-panel .p-inputtext {
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
                border-radius: 8px;
                border-color: #cbd5e1;
                transition: border-color 0.15s ease, box-shadow 0.15s ease;
              }

              .payment-method-dialog-receipt-panel .p-inputtext:enabled:hover {
                border-color: #94a3b8;
              }

              .payment-method-dialog-receipt-panel .p-inputtext:enabled:focus {
                border-color: #0d9488;
                box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.18);
              }

              .payment-method-dialog-receipt-panel .p-inputtextarea {
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
                border-radius: 8px;
                border-color: #cbd5e1;
                transition: border-color 0.15s ease, box-shadow 0.15s ease;
                min-height: 3.25rem;
                resize: vertical;
              }

              .payment-method-dialog-receipt-panel .p-inputtextarea:enabled:hover {
                border-color: #94a3b8;
              }

              .payment-method-dialog-receipt-panel .p-inputtextarea:enabled:focus {
                border-color: #0d9488;
                box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.18);
              }
            `}
          </style>
          <div
            className="payment-method-dialog-receipt-payment-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 35%) minmax(0, 65%)',
              gap: '1.25rem',
              alignItems: 'start',
              width: '100%',
            }}
          >
            <div className="payment-method-dialog-receipt-panel">
              <div className="receipt-panel-header">
                <div className="receipt-panel-icon-wrap" aria-hidden>
                  <i className="pi pi-file-edit" />
                </div>
                <div>
                  <h3 className="receipt-panel-title">פרטי קבלה</h3>
                  <span className="receipt-panel-kicker">
                    יוצגו במסמך הקבלה לאחר השלמת התשלום
                  </span>
                </div>
              </div>

              <div className="receipt-panel-info">
                <i className="pi pi-info-circle" aria-hidden />
                <span>
                  הפרטים הבאים יודפסו בקבלה הרשמית. «לכבוד» ו-«עבור» — עד 100
                  תווים כל אחד; «לכבוד» הוא שם הנמען בכותרת (ברירת מחדל: שם
                  התלמיד ומספר ת.ז). «מס׳ זהות» ו-«טלפון» נדרשים. «עבור»
                  אופציונלי לתיאור מקבל השירות. «הערה» — אופציונלי, עד 250
                  תווים, לפירוט נוסף לתיעוד.
                </span>
              </div>

              <div className="flex flex-column gap-0" style={{ width: '100%' }}>
                <div className="receipt-field-group">
                  <label
                    htmlFor="receipt-recipient-name"
                    className="receipt-field-label"
                  >
                    לכבוד
                  </label>
                  <InputText
                    id="receipt-recipient-name"
                    value={receiptRecipientName}
                    onChange={(e) => setReceiptRecipientName(e.target.value)}
                    className="w-full"
                    maxLength={100}
                    dir="rtl"
                  />
                  <div className="receipt-field-hint">עד 100 תווים</div>
                </div>
                <div className="receipt-field-group">
                  <label
                    htmlFor="receipt-identifier-id"
                    className="receipt-field-label"
                  >
                    מס׳ זהות
                  </label>
                  <InputText
                    id="receipt-identifier-id"
                    value={receiptIdentifierId}
                    style={{ textAlign: 'right' }}
                    onChange={(e) => setReceiptIdentifierId(e.target.value)}
                    className="w-full"
                    dir="ltr"
                  />
                  <div className="receipt-field-hint">
                    ברירת מחדל: ת.ז של התלמיד
                  </div>
                </div>
                <div className="receipt-field-group">
                  <label
                    htmlFor="receipt-phone-number"
                    className="receipt-field-label"
                  >
                    טלפון
                  </label>
                  <InputText
                    id="receipt-phone-number"
                    value={receiptPhoneNumber}
                    style={{ textAlign: 'right' }}
                    onChange={(e) => setReceiptPhoneNumber(e.target.value)}
                    className="w-full"
                    maxLength={10}
                    dir="ltr"
                  />
                  <div className="receipt-field-hint">
                    ברירת מחדל: טלפון התלמיד
                  </div>
                </div>
                <div className="receipt-field-group">
                  <label
                    htmlFor="receipt-issued-for"
                    className="receipt-field-label"
                  >
                    עבור
                    <span className="receipt-optional-pill">אופציונלי</span>
                  </label>
                  <InputText
                    id="receipt-issued-for"
                    value={receiptIssuedFor}
                    onChange={(e) => setReceiptIssuedFor(e.target.value)}
                    className="w-full"
                    maxLength={100}
                    dir="rtl"
                    placeholder="למשל: מבחן תיל"
                  />
                  <div className="receipt-field-hint">עד 100 תווים</div>
                </div>
                <div className="receipt-field-group">
                  <label htmlFor="receipt-note" className="receipt-field-label">
                    הערה
                    <span className="receipt-optional-pill">אופציונלי</span>
                  </label>
                  <InputTextarea
                    id="receipt-note"
                    value={receiptNote}
                    onChange={(e) => setReceiptNote(e.target.value)}
                    className="w-full"
                    maxLength={250}
                    rows={2}
                    autoResize
                    dir="rtl"
                    placeholder="הערה קצרה לתיעוד (אופציונלי)…"
                  />
                  <div className="receipt-field-hint">עד 250 תווים</div>
                </div>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 className="text-xl font-bold mb-4">פרטי תשלום</h3>
              <PaymentMethodForm
                selectedMethod={selectedMethod}
                allocationAmounts={allocationAmounts}
                creditCardTokens={creditCardTokens}
                creditCardData={creditCardData}
                onCreditCardDataChange={setCreditCardData}
                onAddNewCardClick={() => setShowAddNewCardDialog(true)}
                bankTransferData={bankTransferData}
                onBankTransferDataChange={setBankTransferData}
                checkData={checkData}
                onCheckDataChange={setCheckData}
                checks={checks}
                onChecksChange={setChecks}
                scholarshipData={scholarshipData}
                onScholarshipDataChange={setScholarshipData}
                installmentsData={installmentsData}
                onInstallmentsDataChange={setInstallmentsData}
                bankTransferInstallmentsData={bankTransferInstallmentsData}
                onBankTransferInstallmentsDataChange={
                  setBankTransferInstallmentsData
                }
                existingCreditCardInstallments={existingCreditCardInstallments}
                selectedExistingInstallmentPlanId={
                  selectedExistingInstallmentPlanId
                }
                onSelectedExistingInstallmentPlanIdChange={
                  setSelectedExistingInstallmentPlanId
                }
                existingBankTransferInstallments={
                  existingBankTransferInstallments
                }
                selectedExistingBankTransferPlanId={
                  selectedExistingBankTransferPlanId
                }
                onSelectedExistingBankTransferPlanIdChange={
                  setSelectedExistingBankTransferPlanId
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Add New Card Dialog */}
      <AddNewCardDialog
        visible={showAddNewCardDialog}
        onHide={() => setShowAddNewCardDialog(false)}
        userId={user.id}
        onSuccess={() => {
          // Credit card tokens are refreshed inside the dialog
          setShowAddNewCardDialog(false);
        }}
      />
    </Dialog>
  );
};
